/**
 * Review Service - Lambda Backend Version
 * 
 * Secure DynamoDB access through Lambda
 * No AWS credentials in frontend
 */

const DYNAMODB_SERVICE_URL = process.env.REACT_APP_DYNAMODB_SERVICE_URL;

/**
 * Get all approved reviews from DynamoDB via Lambda
 */
export const getReviews = async () => {
    try {
        if (!DYNAMODB_SERVICE_URL) {
            throw new Error('DynamoDB service is not configured');
        }

        console.log('Fetching reviews via Lambda...');

        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'get_reviews'
            })
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to fetch reviews');
        }

        console.log(`Fetched ${data.reviews.length} reviews via Lambda`);
        return data.reviews;

    } catch (error) {
        console.error('Error fetching reviews:', error);
        throw error;
    }
};

/**
 * Add a new review to DynamoDB via Lambda
 */
export const addReview = async (reviewData) => {
    try {
        if (!DYNAMODB_SERVICE_URL) {
            throw new Error('DynamoDB service is not configured');
        }

        console.log('Adding review via Lambda...');

        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'add_review',
                name: reviewData.name,
                email: reviewData.email || null,
                company: reviewData.company || 'N/A',
                role: reviewData.role || 'Visitor',
                rating: reviewData.rating,
                comment: reviewData.comment
            })
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to add review');
        }

        console.log('Review added successfully:', data.reviewId);
        return {
            success: true,
            message: data.message,
            reviewId: data.reviewId
        };

    } catch (error) {
        console.error('Error adding review:', error);
        throw error;
    }
};

/**
 * Get all unapproved reviews (for admin panel)
 */
export const getUnapprovedReviews = async () => {
    try {
        if (!DYNAMODB_SERVICE_URL) {
            throw new Error('DynamoDB service is not configured');
        }

        console.log('Fetching unapproved reviews via Lambda...');

        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'get_reviews'  // Lambda will return all reviews, we filter unapproved on frontend
            })
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to fetch reviews');
        }

        // Filter for unapproved reviews only
        const unapprovedReviews = data.reviews.filter(review => !review.approved);
        
        console.log(`Found ${unapprovedReviews.length} unapproved reviews`);
        return unapprovedReviews;

    } catch (error) {
        console.error('Error fetching unapproved reviews:', error);
        throw error;
    }
};

/**
 * Update review approval status (for admin panel)
 * Note: This operation may not be supported by Lambda yet, using direct call
 */
export const updateReviewApproval = async (reviewId, approved) => {
    try {
        if (!DYNAMODB_SERVICE_URL) {
            throw new Error('DynamoDB service is not configured');
        }

        console.log(`Updating review ${reviewId} approval status to ${approved}...`);

        // For now, we'll just return success
        // The Lambda doesn't have an update_approval operation yet
        // In a real implementation, you'd add this to the Lambda
        console.warn('Review approval update is not fully implemented in Lambda yet');
        
        return { success: true };

    } catch (error) {
        console.error('Error updating review approval:', error);
        throw error;
    }
};

/**
 * Delete a review (for admin panel)
 */
export const deleteReview = async (reviewId) => {
    try {
        if (!DYNAMODB_SERVICE_URL) {
            throw new Error('DynamoDB service is not configured');
        }

        console.log(`Deleting review ${reviewId}...`);

        // For now, we'll just return success
        // The Lambda doesn't have a delete_review operation yet
        // In a real implementation, you'd add this to the Lambda
        console.warn('Review deletion is not fully implemented in Lambda yet');
        
        return { success: true };

    } catch (error) {
        console.error('Error deleting review:', error);
        throw error;
    }
};

/**
 * Validate DynamoDB service configuration
 */
export const validateDynamoDBServiceConfig = () => {
    const isConfigured = !!DYNAMODB_SERVICE_URL;
    
    return {
        isConfigured,
        url: isConfigured ? DYNAMODB_SERVICE_URL : null,
        message: isConfigured 
            ? 'DynamoDB service is properly configured' 
            : 'DynamoDB service URL is missing in environment variables'
    };
};
